// Safety gate for the ENG-5241 SMB booking-fee disclosure (M1).
//
// An ER-originated SMB consultation is charged the $350 booking fee (folded into
// the Stripe hold) on expert confirmation. The fee is single-sourced from
// site_settings (admin-editable) and fetched live, so the displayed amount can
// never drift from what is charged. If that fetch is unresolved or fails, the
// buyer must NOT be able to submit — submitting would commit them to a charge they
// never saw (the charge != display bug this feature exists to prevent). The gate
// is satisfied only by a real fetched fee, never by a hardcoded fallback.

export interface BookingFeeGateInput {
  // True when the consultation originates from an expert request — the only case
  // that incurs and discloses the booking fee.
  hasExpertRequest: boolean;
  // True once the fee fetch has resolved to a real value (the amount itself is
  // irrelevant — a legitimately-fetched $0 fee, e.g. an already-engaged expert,
  // counts as fetched and must NOT block submit).
  bookingFeeFetched: boolean;
  // True when the fee fetch hard-failed (network / auth / transient / null result).
  bookingFeeError: boolean;
}

export interface BookingFeeGateResult {
  // Block submission until the ER fee is fetched and shown.
  submitBlocked: boolean;
  // Surface the "fee applies but couldn't load — retry" affordance.
  showRetry: boolean;
}

export function bookingFeeGate({
  hasExpertRequest,
  bookingFeeFetched,
  bookingFeeError,
}: BookingFeeGateInput): BookingFeeGateResult {
  return {
    // Gate on fetch state, never on the amount: block until a real fee has been
    // fetched (a failed fetch is not fetched, so it stays blocked until retry
    // succeeds; a fetched $0 fee unblocks).
    submitBlocked: hasExpertRequest && !bookingFeeFetched,
    showRetry: hasExpertRequest && bookingFeeError,
  };
}
